import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  MAX_USERNAME_LENGTH,
  ipfGUIRoleKey,
  secureUserSchemas,
} from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { FormLayout } from 'components/FormLayout/FormLayout';
import { FormikMultiSelectField } from 'components/FormikMultiSelectField/FormikMultiSelectField';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import { NewPasswordRequirementDescription } from 'pages/Me/UserProfile/NewPasswordRequirementDescription';
import type { UpdatePasswordFormValues } from 'pages/Settings/Administration/LocalUsers/UpdatePasswordForm';
import { validateUserPassword } from 'pages/Settings/Administration/LocalUsers/helpers/validateUserPassword';
import { useRolesOptions } from 'pages/Settings/Administration/LocalUsers/useRolesOptions';
import type React from 'react';
import { useMemo } from 'react';
import deepEqual from 'react-fast-compare';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { timezoneOptions } from 'utils/timezoneOptions';

// any change in fields of EditedUser should be reflected
// in webDependencies of actions column on LocalUsersPage
export type EditedUser = {
  id: string;
  isLocal: boolean;
  username: string;
  timezone: string;
  roleIds: string[];
};

type Props = {
  editedUser: EditedUser | 'new';
  onSaveSuccess?: () => void;
};

type LocalUserFormValues = UpdatePasswordFormValues & {
  username: string;
  timezone: string;
  roleIds: string[];
  newPassword: string;
  retypePassword: string;
};

export const LocalUserForm: React.FC<Props> = (props) => {
  const [submitting, setSubmitting] = useLoadingState();
  const formMsg = useLocale().localUsers.form;
  const api = useApi();
  const notifications = useNotifications();

  const handleSubmit = async (values: LocalUserFormValues) => {
    await withLoadingIndicator(setSubmitting)(async () => {
      const commonData = {
        timezone: values.timezone,
        ...((props.editedUser === 'new' || props.editedUser.isLocal) && {
          roleIds: values.roleIds,
        }),
      };
      if (props.editedUser === 'new') {
        await api.request({
          url: '/users',
          method: 'POST',
          data: {
            ...commonData,
            username: values.username,
            password: values.newPassword,
          },
        });
        notifications.showNotification({
          type: 'success',
          message: formMsg.userCreated,
        });
      } else {
        await api.request({
          url: `/users/${props.editedUser.id}`,
          method: 'PATCH',
          data: commonData,
        });
        notifications.showNotification({
          type: 'success',
          message: formMsg.userUpdated,
        });
      }
      props.onSaveSuccess?.();
    });
  };

  const validate = (
    values: LocalUserFormValues,
  ): FormikErrors<LocalUserFormValues> => {
    const errors: FormikErrors<LocalUserFormValues> = {};
    if (props.editedUser === 'new') {
      if (!values.username) {
        errors.username = formMsg.validations.required;
      } else if (
        !secureUserSchemas.username.safeParse(values.username).success
      ) {
        errors.username =
          values.username.length > MAX_USERNAME_LENGTH
            ? formMsg.validations.usernameMaxLength
            : formMsg.validations.usernameNotAllowedCharacters;
      }

      validateUserPassword(values, errors, formMsg.validations);
    }
    if (!values.timezone) {
      errors.timezone = formMsg.validations.required;
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      username: props.editedUser === 'new' ? '' : props.editedUser.username,
      timezone: props.editedUser === 'new' ? '' : props.editedUser.timezone,
      roleIds:
        props.editedUser === 'new' ? [ipfGUIRoleKey] : props.editedUser.roleIds,
      newPassword: '',
      retypePassword: '',
    },
    validate,
    onSubmit: makeFormikOnSubmit(handleSubmit),
    enableReinitialize: true,
  });

  const rolesOptions = useRolesOptions();
  const changed = useMemo(
    () => !deepEqual(formik.initialValues, formik.values),
    [formik.initialValues, formik.values],
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormLayout width='full' noPadding>
        <header>
          <h2>{formMsg.title}</h2>
        </header>
        {props.editedUser === 'new' && (
          <FormikTextField
            autoComplete='off'
            name='username'
            formik={formik}
            disabled={props.editedUser !== 'new'}
            type='text'
            label={formMsg.fields.username}
          />
        )}
        <FormikSelectField
          name='timezone'
          options={timezoneOptions}
          formik={formik}
          label={formMsg.fields.timezone}
          searchable
          nonClearable
          dataTestId='local-user-timezone-select'
        />
        {(props.editedUser === 'new' || props.editedUser.isLocal) && (
          <FormikMultiSelectField
            name='roleIds'
            formik={formik}
            searchable
            label={formMsg.fields.roleIds}
            loading={rolesOptions === null}
            options={rolesOptions ?? []}
            dataTestId='local-user-roles-select'
          />
        )}
        {props.editedUser === 'new' && (
          <>
            <FormikTextField
              className='max-w-96'
              name='newPassword'
              formik={formik}
              type='password'
              label={formMsg.fields.password}
              description={<NewPasswordRequirementDescription />}
            />
            <FormikTextField
              className='max-w-96'
              name='retypePassword'
              formik={formik}
              type='password'
              label={formMsg.fields.retypePassword}
            />
          </>
        )}
        <footer>
          <Button
            disabled={!changed}
            loading={submitting === 'LOADING'}
            type='submit'
            variant='primary'
            size='sm'
            icon='fa-floppy-disk'
            dataTestId='local-user-save-button'
          >
            {props.editedUser === 'new'
              ? formMsg.createUserButton
              : formMsg.updateUserButton}
          </Button>
        </footer>
      </FormLayout>
    </form>
  );
};
